import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth.js';
import { withOfflineFallback } from '../utils/offline.js';
import { db } from '../db/dexie.js';
import api from '../services/auth.service.js';
import type { Atividade, AtividadeFormInput } from '@azoupvendas/shared';

// AIDEV-NOTE: Chave de query centralizada para invalidação consistente entre componentes
export const ATIVIDADES_QUERY_KEY = 'atividades';

interface UseAtividadesOptions {
  leadId?: string;
  clienteCodigo?: number;
  status?: 'pendente' | 'realizada' | 'cancelada';
}

interface UpdateAtividadeInput extends Partial<AtividadeFormInput> {
  id: string;
}

export function useAtividades(opts: UseAtividadesOptions = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [ATIVIDADES_QUERY_KEY, user?.client_id, user?.vendedor_id, opts],
    enabled: !!user,
    queryFn: () =>
      withOfflineFallback(
        async () => {
          const params: Record<string, string> = {};
          if (opts.leadId) params.lead_id = opts.leadId;
          if (opts.clienteCodigo) params.cliente_codigo = String(opts.clienteCodigo);
          if (opts.status) params.status = opts.status;

          const res = await api.get<{ atividades: Atividade[] }>('/atividades', { params });
          return res.data.atividades;
        },
        async () => {
          // AIDEV-NOTE: Fallback offline — tabela atividades existe no schema Dexie v2
          const vendedorId = user!.vendedor_id;
          if (!vendedorId) return [] as Atividade[];
          return db.atividades
            .where('vendedor_id')
            .equals(vendedorId)
            .toArray() as unknown as Promise<Atividade[]>;
        }
      )
  });

  const createAtividade = useMutation({
    mutationFn: async (input: AtividadeFormInput) => {
      const res = await api.post<{ atividade: Atividade }>('/atividades', input);
      return res.data.atividade;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ATIVIDADES_QUERY_KEY] })
  });

  const completeAtividade = useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/atividades/${id}/complete`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ATIVIDADES_QUERY_KEY] })
  });

  const updateAtividade = useMutation({
    mutationFn: async ({ id, ...input }: UpdateAtividadeInput) => {
      const res = await api.patch<{ atividade: Atividade }>(`/atividades/${id}`, input);
      return res.data.atividade;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ATIVIDADES_QUERY_KEY] })
  });

  const deleteAtividade = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/atividades/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ATIVIDADES_QUERY_KEY] })
  });

  return {
    atividades: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,
    createAtividade,
    completeAtividade,
    updateAtividade,
    deleteAtividade
  };
}
